import { useState, useEffect } from 'react';
import Navbar from '../components/Navbar.jsx';
import { api } from '../api/client.js';
import RoleMappings from '../components/RoleMappings.jsx';

export default function DirectorySettings() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get('/api/directory/status');
      setStatus(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30_000);
    return () => clearInterval(t);
  }, []);

  const triggerSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      await api.post('/api/directory/sync');
      await refresh();
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || e.message);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="max-w-4xl mx-auto px-6 py-8">Loading…</main>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-medium mb-1">Google Workspace Directory</h1>
        <p className="text-sm text-muted mb-6">
          Syncs users, groups, and organizational units from Google Workspace into the
          Launchpad's local cache. Used by the access policy engine to authorize app
          launches by user, group, or OU.
        </p>

        {!status?.configured && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-amber-900 mb-1">
              Directory sync is not configured
            </p>
            <p className="text-xs text-amber-800">
              Set these env vars on the server and restart:{' '}
              <code className="font-mono">GOOGLE_SERVICE_ACCOUNT_KEY_PATH</code>,{' '}
              <code className="font-mono">GOOGLE_ADMIN_DELEGATE_EMAIL</code>,{' '}
              <code className="font-mono">GOOGLE_WORKSPACE_DOMAIN</code>. See{' '}
              <code>DIRECTORY_SETUP.md</code> in the repo for the full setup guide.
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Counts */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <Tile label="Active users" value={status.counts.users} />
          <Tile label="Groups" value={status.counts.groups} />
          <Tile label="Org units" value={status.counts.ous} />
          <Tile label="Memberships" value={status.counts.memberships} />
        </div>

        {/* Sync controls */}
        <div className="bg-white border border-border rounded-xl p-6 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-medium mb-1">Manual sync</h2>
              <p className="text-xs text-muted">
                Pulls fresh data from Google Workspace immediately. Auto-runs every 60
                minutes in the background.
              </p>
            </div>
            <button
              onClick={triggerSync}
              disabled={syncing || !status.configured}
              className="text-sm bg-ink text-white rounded-lg px-4 py-2 disabled:opacity-50"
            >
              {syncing ? 'Syncing…' : 'Sync now'}
            </button>
          </div>
        </div>

        {/* Role mappings */}
        <RoleMappings />

        {/* Last sync */}
        {status.lastSync && (
          <div className="bg-white border border-border rounded-xl p-6">
            <h2 className="text-base font-medium mb-3">Last sync</h2>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-muted">Status</dt>
              <dd>
                <StatusBadge status={status.lastSync.status} />
              </dd>
              <dt className="text-muted">Started</dt>
              <dd>{new Date(status.lastSync.startedAt).toLocaleString()}</dd>
              <dt className="text-muted">Finished</dt>
              <dd>
                {status.lastSync.finishedAt
                  ? new Date(status.lastSync.finishedAt).toLocaleString()
                  : '—'}
              </dd>
              <dt className="text-muted">Users synced</dt>
              <dd>{status.lastSync.usersSynced}</dd>
              <dt className="text-muted">Groups synced</dt>
              <dd>{status.lastSync.groupsSynced}</dd>
              <dt className="text-muted">OUs synced</dt>
              <dd>{status.lastSync.ousSynced}</dd>
              <dt className="text-muted">Memberships synced</dt>
              <dd>{status.lastSync.membershipsSynced}</dd>
              {status.lastSync.errorMessage && (
                <>
                  <dt className="text-muted">Error</dt>
                  <dd className="text-red-700">{status.lastSync.errorMessage}</dd>
                </>
              )}
            </dl>
          </div>
        )}
      </main>
    </>
  );
}

function Tile({ label, value }) {
  return (
    <div className="bg-neutral-50 rounded-xl p-4">
      <div className="text-2xl font-medium text-ink">{value}</div>
      <div className="text-xs text-muted mt-1">{label}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    success: { bg: '#E1F5EE', fg: '#085041' },
    error:   { bg: '#FCEBEB', fg: '#791F1F' },
    running: { bg: '#FAEEDA', fg: '#633806' }
  };
  const s = styles[status] || styles.running;
  return (
    <span
      className="inline-block text-xs font-medium px-2 py-0.5 rounded-full"
      style={{ background: s.bg, color: s.fg }}
    >
      {status}
    </span>
  );
}
